import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type WindowState = 'normal' | 'minimized' | 'maximized';

export interface FormWindow {
  id: string;
  title: string;
  component: any;
  state: WindowState;
  position: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class WindowManagerService {
  private windowsSubject = new BehaviorSubject<FormWindow[]>([]);
  private nextZIndex = 1000;
  private windowCounter = 0;

  windows$: Observable<FormWindow[]> = this.windowsSubject.asObservable();

  /**
   * Open a new form window
   */
  openWindow(title: string, component: any, data?: any): string {
    const windowId = `window_${++this.windowCounter}_${Date.now()}`;
    const windows = this.windowsSubject.value;
    
    const newWindow: FormWindow = {
      id: windowId,
      title,
      component,
      state: 'normal',
      position: { 
        x: 50 + (windows.length * 30), 
        y: 50 + (windows.length * 30) 
      },
      size: { width: 900, height: 700 },
      zIndex: this.nextZIndex++,
      data,
    };

    this.windowsSubject.next([...windows, newWindow]);
    return windowId;
  }

  /**
   * Close a window
   */
  closeWindow(windowId: string): void {
    const windows = this.windowsSubject.value.filter(w => w.id !== windowId);
    this.windowsSubject.next(windows);
  }

  /**
   * Minimize a window
   */
  minimizeWindow(windowId: string): void {
    const windows = this.windowsSubject.value.map(w => 
      w.id === windowId ? { ...w, state: 'minimized' as WindowState } : w
    );
    this.windowsSubject.next(windows);
  }

  /**
   * Maximize a window
   */
  maximizeWindow(windowId: string): void {
    const windows = this.windowsSubject.value.map(w => 
      w.id === windowId ? { ...w, state: 'maximized' as WindowState } : w
    );
    this.windowsSubject.next(windows);
  }

  /**
   * Restore a window to normal state
   */
  restoreWindow(windowId: string): void {
    const windows = this.windowsSubject.value.map(w => 
      w.id === windowId ? { ...w, state: 'normal' as WindowState } : w
    );
    this.windowsSubject.next(windows);
  }

  /**
   * Bring window to front
   */
  bringToFront(windowId: string): void {
    const windows = this.windowsSubject.value.map(w => 
      w.id === windowId ? { ...w, zIndex: this.nextZIndex++ } : w
    );
    this.windowsSubject.next(windows);
  }

  /**
   * Update window position
   */
  updateWindowPosition(windowId: string, position: { x: number; y: number }): void {
    const windows = this.windowsSubject.value.map(w => 
      w.id === windowId ? { ...w, position } : w
    );
    this.windowsSubject.next(windows);
  }

  /**
   * Update window size
   */
  updateWindowSize(windowId: string, size: { width: number; height: number }): void {
    const windows = this.windowsSubject.value.map(w => 
      w.id === windowId ? { ...w, size } : w
    );
    this.windowsSubject.next(windows);
  }

  /**
   * Get minimized windows
   */
  getMinimizedWindows(): FormWindow[] {
    return this.windowsSubject.value.filter(w => w.state === 'minimized');
  }

  /**
   * Get active (non-minimized) windows
   */
  getActiveWindows(): FormWindow[] {
    return this.windowsSubject.value.filter(w => w.state !== 'minimized');
  }
}

